import React, { useCallback } from 'react';
import { MediaItem } from '@/core/MediaItem/MediaItem.model';
import { MediaCardContainer } from '@/components/MediaCard/MediaCardContainer';
import { MediaDisplay } from '@/components/MediaCard/MediaDisplay';
import * as Overlay from '@/components/MediaCard/MediaOverlay';
import * as Badge from '@/components/MediaCard/MediaBadges';
import * as Action from '@/components/MediaCard/MediaActions';
import { useMediaDownload } from '@/core/MediaItem/useMediaDownload';

interface EditorMediaCardProps {
  item: MediaItem;
  onMediaClick: (item: MediaItem) => void;
}

export const EditorMediaCard: React.FC<EditorMediaCardProps> = ({
  item,
  onMediaClick,
}) => {
  const { downloadStatus, isDownloading, download } = useMediaDownload();
  const aspectRatio = item.isVideo() ? "9:16" : "4:5";

  const handleCardClick = () => {
    onMediaClick(item);
  };

  const handleDownloadClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await download(item);
  }, [item, download]);

  return (
    <MediaCardContainer
      aspectRatio={aspectRatio}
      selected={false}
      disabled={false}
      onClick={handleCardClick}
    >
      <MediaDisplay
        mediaItem={item}
        useThumbnail={true}
        showPlayButton={true}
      />

      {/* Top-right: Download button */}
      <Overlay.TopRight showOnHover>
        <Action.DownloadButton
          onClick={handleDownloadClick}
          loading={downloadStatus === 'checking' || downloadStatus === 'downloading'}
        />
      </Overlay.TopRight>

      {/* Bottom-left: Label badge */}
      <Overlay.BottomLeft>
        {item.label && <Badge.LabelBadge label={item.label} />}
      </Overlay.BottomLeft>

      {/* Bottom-right: Download status or Duration badge for videos */}
      <Overlay.BottomRight>
        {downloadStatus ? (
          <Badge.DownloadStatusBadge status={downloadStatus} />
        ) : (
          item.durationSeconds && (
            <Badge.DurationBadge durationSeconds={item.durationSeconds} />
          )
        )}
      </Overlay.BottomRight>
    </MediaCardContainer>
  );
};
